<template>
  <v-card
    variant="outlined"
    rounded="lg"
    class="portal-turno-pac-card h-100 d-flex flex-column bg-surface"
    :class="{
      'portal-turno-pac-card--list': display === 'list',
      'portal-turno-pac-card--destacada': destacada,
    }"
  >
    <v-card-text
      class="portal-turno-pac-card__body flex-grow-1 d-flex flex-column pt-4 pb-0 pr-3"
      :class="{ 'pb-2': !mostrarAcciones }"
    >
      <div class="portal-turno-pac-card__main min-w-0">
        <div
          class="portal-turno-pac-card__header d-flex justify-space-between align-start gap-2 mb-2"
        >
          <div class="portal-turno-pac-card__title text-body-1 font-weight-bold min-w-0">
            {{ item.especialidad }}
          </div>
          <v-chip
            size="small"
            :color="estadoChipColor"
            variant="tonal"
            class="text-none flex-shrink-0 font-weight-bold"
          >
            {{ item.estadoLabel }}
          </v-chip>
        </div>

        <v-alert
          v-if="mostrarAvisoSinConfirmar"
          type="info"
          color="secondary"
          variant="tonal"
          density="compact"
          rounded="lg"
          class="portal-turno-pac-card__confirm-alert portal-turno-pac-card__confirm-alert--card text-body-2 mb-2"
        >
          <template #prepend>
            <v-icon :icon="infoAlertIcon" color="secondary" size="18" />
          </template>
          {{ avisoSinConfirmarTexto }}
        </v-alert>

        <div class="portal-turno-pac-card__content-row">
          <div class="portal-turno-pac-card__details d-flex flex-column gap-2 flex-grow-1">
            <div
              v-for="row in detailRows"
              :key="row.key"
              class="portal-turno-pac-card__detail d-flex align-center gap-3 min-w-0 mb-1"
            >
              <v-avatar
                size="36"
                variant="tonal"
                color="secondary"
                class="portal-turno-pac-card__row-avatar flex-shrink-0 mr-2"
              >
                <v-icon :icon="row.icon" size="20" />
              </v-avatar>
              <span
                class="portal-turno-pac-card__detail-text text-body-2 text-medium-emphasis min-w-0"
              >
                {{ row.text }}
              </span>
            </div>
          </div>

          <div class="portal-turno-pac-card__summary-line min-w-0">
            <span
              v-for="row in detailRows"
              :key="`summary-${row.key}`"
              class="portal-turno-pac-card__summary-item min-w-0"
              :class="`portal-turno-pac-card__summary-item--${row.key}`"
            >
              <span class="portal-turno-pac-card__summary-avatar">
                <v-icon :icon="row.icon" size="18" color="secondary" />
              </span>
              <span class="portal-turno-pac-card__summary-text text-body-2 text-medium-emphasis">
                {{ row.text }}
              </span>
            </span>
          </div>

          <v-card-actions
            v-if="mostrarAcciones"
            class="portal-turno-pac-card__actions portal-turno-pac-card__actions--inline px-4 py-1 w-100"
          >
            <v-btn
              v-if="mostrarAccionPreparacion"
              block
              variant="outlined"
              color="primary"
              size="small"
              class="portal-turno-pac-card__action-btn text-none"
              :loading="preparacionLoading"
              @click="$emit('ver-preparacion')"
            >
              Ver preparación
            </v-btn>
            <v-btn
              v-if="mostrarAccionCancelar"
              block
              variant="outlined"
              color="error"
              size="small"
              class="portal-turno-pac-card__action-btn text-none"
              @click="$emit('cancelar')"
            >
              Cancelar turno
            </v-btn>
          </v-card-actions>
        </div>

        <v-alert
          v-if="mostrarAvisoSinConfirmar"
          type="info"
          color="secondary"
          variant="tonal"
          density="compact"
          rounded="lg"
          class="portal-turno-pac-card__confirm-alert portal-turno-pac-card__confirm-alert--list text-body-2 mb-0"
        >
          <template #prepend>
            <v-icon :icon="infoAlertIcon" color="secondary" size="18" />
          </template>
          {{ avisoSinConfirmarTexto }}
        </v-alert>
      </div>
    </v-card-text>

    <v-card-actions
      v-if="mostrarAcciones"
      class="portal-turno-pac-card__actions portal-turno-pac-card__actions--stacked pt-0 px-4 pb-2 w-100"
    >
      <v-btn
        v-if="mostrarAccionPreparacion"
        block
        variant="outlined"
        color="primary"
        size="small"
        class="portal-turno-pac-card__action-btn text-none"
        :loading="preparacionLoading"
        @click="$emit('ver-preparacion')"
      >
        Ver preparación
      </v-btn>
      <v-btn
        v-if="mostrarAccionCancelar"
        block
        variant="outlined"
        color="error"
        size="small"
        class="portal-turno-pac-card__action-btn text-none"
        @click="$emit('cancelar')"
      >
        Cancelar turno
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import icons from '../../../utils/enums/icons'

export default {
  name: 'PortalTurnoPacienteCard',
  emits: ['cancelar', 'ver-preparacion'],
  props: {
    item: {
      type: Object,
      required: true,
    },
    /** Turnos pendientes (no historial): muestra acción cancelar si no está cancelado */
    permiteCancelar: {
      type: Boolean,
      default: false,
    },
    preparacionLoading: {
      type: Boolean,
      default: false,
    },
    display: {
      type: String,
      default: 'card',
      validator: (v) => v === 'card' || v === 'list',
    },
    destacada: {
      type: Boolean,
      default: false,
    },
    noConfirmarAutomaticamenteTurnos: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      doctorIcon: icons.DOCTOR,
      calendarIcon: icons.CALENDAR,
      clockIcon: icons.CLOCK_OUTLINE,
      mapIcon: icons.MAP_MARKER,
      videoConsultaIcon: icons.VIDEO,
      groupIcon: icons.GROUP,
      obraSocialIcon: icons.CARD_ACCOUNT_DETAILS_OUTLINE,
      infoAlertIcon: icons.SNB_INFO,
    }
  },
  computed: {
    mostrarAcciones() {
      return this.mostrarAccionPreparacion || this.mostrarAccionCancelar
    },
    mostrarAccionPreparacion() {
      return (
        this.permiteCancelar &&
        !this.item.cancelado &&
        !this.item.esHistorial &&
        this.item.puedeConsultarPreparacion
      )
    },
    mostrarAccionCancelar() {
      return (
        this.permiteCancelar &&
        !this.item.cancelado &&
        !this.item.esHistorial
      )
    },
    mostrarAvisoSinConfirmar() {
      return (
        !this.item.cancelado &&
        !this.item.esHistorial &&
        this.item.confirmado === false
      )
    },
    avisoSinConfirmarTexto() {
      return "Recibirá las indicaciones para confirmar el turno a través de los medios de contacto registrados."
    },
    estadoChipColor() {
      if (this.item.cancelado) return 'error'
      if (this.item.esHistorial) {
        return this.item.asistio ? 'success' : 'grey'
      }
      if (this.item.confirmado) return 'success'
      return 'warning'
    },
    detailRows() {
      const { item } = this
      const rows = [
        { key: 'm', icon: this.doctorIcon, text: item.medico },
        { key: 'f', icon: this.calendarIcon, text: item.fechaDisplay },
        { key: 'h', icon: this.clockIcon, text: item.horaDisplay },
        {
          key: 'l',
          icon: item.esVideoconsulta ? this.videoConsultaIcon : this.mapIcon,
          text: item.lugar,
        },
        {
          key: 'os',
          icon: this.obraSocialIcon,
          text: item.osPlanDisplay ?? '—',
        },
      ]
      if (item.esTurnoFamiliar && item.pacienteDisplay) {
        rows.unshift({
          key: 'paciente',
          icon: this.groupIcon,
          text: `${item.pacienteDisplay}`,
        })
      }
      return rows
    },
  },
}
</script>

<style scoped>
.portal-turno-pac-card {
  border-color: #eceff1 !important;
  container-type: inline-size;
}
.portal-turno-pac-card--destacada {
  position: relative;
  overflow: visible !important;
  border-color: #eceff1 !important;
  box-shadow: 0 8px 26px rgba(var(--v-theme-primary), 0.18) !important;
  transition:
    box-shadow 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}
.portal-turno-pac-card--destacada::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  z-index: 0;
  box-shadow:
    inset 0 0 0 2px rgba(var(--v-theme-primary), 0.72),
    0 0 0 0 rgba(var(--v-theme-primary), 0.34);
  animation: portalTurnoProximoDestacado 1.25s ease-in-out 3 forwards;
}
.portal-turno-pac-card--destacada > * {
  position: relative;
  z-index: 1;
}
.portal-turno-pac-card--destacada:hover {
  transform: translateY(-3px);
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.12),
    0 4px 12px rgba(0, 0, 0, 0.06),
    0 0 0 1px rgba(var(--v-theme-primary), 0.14),
    0 0 22px rgba(var(--v-theme-primary), 0.1) !important;
}
.portal-dark-mode .portal-turno-pac-card,
.portal-dark-mode .portal-turno-pac-card--destacada {
  border-color: rgba(255, 255, 255, 0.1) !important;
}
.portal-dark-mode .portal-turno-pac-card--destacada {
  box-shadow: 0 8px 26px rgba(var(--v-theme-secondary), 0.18) !important;
}
.portal-dark-mode .portal-turno-pac-card--destacada::before {
  box-shadow:
    inset 0 0 0 2px rgba(var(--v-theme-secondary), 0.72),
    0 0 0 0 rgba(var(--v-theme-secondary), 0.34);
  animation-name: portalTurnoProximoDestacadoDark;
}
.portal-dark-mode .portal-turno-pac-card--destacada:hover {
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.36),
    0 4px 12px rgba(0, 0, 0, 0.22),
    0 0 0 1px rgba(var(--v-theme-secondary), 0.14),
    0 0 22px rgba(var(--v-theme-secondary), 0.1) !important;
}
@keyframes portalTurnoProximoDestacado {
  0%,
  100% {
    opacity: 1;
    box-shadow:
      inset 0 0 0 2px rgba(var(--v-theme-primary), 0.72),
      0 0 0 0 rgba(var(--v-theme-primary), 0.28);
  }
  50% {
    opacity: 0.72;
    box-shadow:
      inset 0 0 0 2px rgba(var(--v-theme-primary), 0.72),
      0 0 0 5px rgba(var(--v-theme-primary), 0.18);
  }
}
@keyframes portalTurnoProximoDestacadoDark {
  0%,
  100% {
    opacity: 1;
    box-shadow:
      inset 0 0 0 2px rgba(var(--v-theme-secondary), 0.72),
      0 0 0 0 rgba(var(--v-theme-secondary), 0.28);
  }
  50% {
    opacity: 0.72;
    box-shadow:
      inset 0 0 0 2px rgba(var(--v-theme-secondary), 0.72),
      0 0 0 5px rgba(var(--v-theme-secondary), 0.18);
  }
}
.portal-turno-pac-card__main {
  width: 100%;
}
.portal-turno-pac-card__row-avatar {
  align-self: center;
}
.portal-turno-pac-card__detail-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.portal-turno-pac-card__summary-line {
  display: none;
}
.portal-turno-pac-card__confirm-alert {
  line-height: 1.25;
}
.portal-turno-pac-card__confirm-alert :deep(.v-alert__prepend) {
  margin-inline-end: 8px;
}
.portal-turno-pac-card__confirm-alert :deep(.v-alert__content) {
  width: 100%;
}
.portal-turno-pac-card__confirm-alert :deep(.v-alert__underlay) {
  opacity: 0.12;
}
.portal-turno-pac-card__confirm-alert--list {
  display: none;
}
.portal-turno-pac-card__actions {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
}
.portal-turno-pac-card__actions--inline {
  display: none !important;
}
.portal-turno-pac-card__actions--stacked {
  align-items: center;
}
.portal-turno-pac-card__actions--inline {
  align-items: center;
}
.portal-turno-pac-card__actions :deep(.v-btn) {
  flex: 1 1 calc(50% - 4px);
  min-width: 0;
  max-width: 100%;
  width: auto !important;
  margin-inline-start: 0 !important;
}
.portal-turno-pac-card__actions :deep(.portal-turno-pac-card__action-btn) {
  min-height: 28px;
  height: 28px;
  align-items: center;
}
.portal-turno-pac-card__actions :deep(.portal-turno-pac-card__action-btn .v-btn__content) {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  line-height: 1.1;
  text-align: center;
}
@container (max-width: 285px) {
  .portal-turno-pac-card__actions--stacked {
    flex-direction: column;
  }
  .portal-turno-pac-card__actions--stacked :deep(.v-btn) {
    flex-basis: auto;
    width: 100% !important;
  }
}
@media (max-width: 599px) {
  .portal-turno-pac-card__actions--stacked {
    flex-direction: column;
    align-items: stretch;
  }
  .portal-turno-pac-card__actions--stacked :deep(.v-btn) {
    flex: 0 0 auto;
    width: 100% !important;
  }
  .portal-turno-pac-card__actions--stacked :deep(.portal-turno-pac-card__action-btn) {
    min-height: 30px;
    height: auto;
    padding-top: 4px;
    padding-bottom: 4px;
  }
  .portal-turno-pac-card__actions--stacked :deep(.portal-turno-pac-card__action-btn .v-btn__content) {
    white-space: normal;
  }
  .portal-turno-pac-card__actions--stacked :deep(.v-btn:first-child) {
    margin-top: 4px;
  }
}
@media (min-width: 960px) {
  .portal-turno-pac-card--list {
    height: auto !important;
    min-height: 0;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__body {
    flex: 0 0 auto !important;
    min-width: 0;
    flex-direction: column !important;
    gap: 0;
    padding: 8px 10px !important;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__main {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__header {
    flex-direction: row;
    justify-content: space-between !important;
    align-items: flex-end !important;
    gap: 8px !important;
    margin-bottom: 2px !important;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__title {
    font-size: 1rem;
    line-height: 1.15;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__content-row {
    display: flex;
    align-items: flex-end;
    flex-wrap: nowrap;
    gap: 12px;
    min-width: 0;
    margin-top: -1px;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__confirm-alert--card {
    display: none;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__confirm-alert--list {
    display: flex;
    margin-top: 8px;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__confirm-alert {
    min-height: 0;
    padding: 2px 8px !important;
  }
  .portal-turno-pac-card--list
    .portal-turno-pac-card__confirm-alert
    :deep(.v-alert__prepend) {
    align-self: center;
    margin-inline-end: 6px;
  }
  .portal-turno-pac-card--list
    .portal-turno-pac-card__confirm-alert
    :deep(.v-alert__content) {
    font-size: 0.8125rem;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__details {
    display: none !important;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-line {
    display: flex;
    flex-wrap: wrap;
    flex: 1 1 auto;
    min-width: 0;
    align-items: center;
    gap: 8px 14px;
    line-height: 1.15;
    overflow: visible;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-item {
    display: inline-flex;
    flex: 0 1 auto;
    align-items: center;
    min-width: 0;
    max-width: none;
    gap: 7px;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-item--f {
    flex: 0 0 auto;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-item--h {
    flex: 0 0 auto;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-item--m,
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-item--paciente {
    flex: 0 1 auto;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-item--l {
    flex: 0 1 auto;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-item--os {
    flex: 0 1 auto;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-avatar {
    width: 30px;
    height: 30px;
    min-width: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: rgba(var(--v-theme-secondary), 0.12);
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-item:last-child {
    max-width: none;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-text {
    min-width: 0;
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
    overflow-wrap: normal;
  }
  .portal-turno-pac-card--list
    .portal-turno-pac-card__summary-item:last-child
    .portal-turno-pac-card__summary-text {
    overflow: visible;
    text-overflow: clip;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__row-avatar {
    width: 28px !important;
    height: 28px !important;
    min-width: 28px !important;
    margin-right: 4px !important;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__detail-text {
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__actions--stacked {
    display: none;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__actions--inline {
    display: flex !important;
    flex: 0 0 auto;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
    align-self: flex-end;
    justify-content: flex-end;
    gap: 8px;
    width: auto !important;
    padding: 0 !important;
    margin-bottom: 2px;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__actions--inline :deep(.v-btn) {
    flex: 0 1 auto;
    min-height: 26px;
    height: 28px;
    min-width: 118px;
    padding: 0 10px;
    width: auto !important;
  }
}
@media (min-width: 960px) and (max-width: 1279.98px) {
  .portal-turno-pac-card--list .portal-turno-pac-card__content-row {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px 12px;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-line {
    flex: 1 1 100%;
    flex-wrap: wrap;
    overflow: visible;
    gap: 8px 12px;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-item {
    flex: 0 1 auto;
    max-width: none;
    align-items: center;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__summary-text {
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
    overflow-wrap: anywhere;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__actions--inline {
    flex: 0 0 100%;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  }
  .portal-turno-pac-card--list .portal-turno-pac-card__actions--inline :deep(.v-btn) {
    flex: 0 0 auto;
    width: min(180px, 100%) !important;
  }
}
</style>
